import { ReactElement, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useFlags } from '@flagsmith/flagsmith/react';
import { BreakpointMedia } from '../../utils/config';
import {
  useProductItemQuery,
  useProductRecommendationsQuery,
} from '../../services/product/product.queries';
import ReactivationBannerContainer from '../shared/banner/ReactivationBannerContainer';
import { useUser } from '../../contexts/UserContext';
import { SubscriptionStatus } from '../../models/user.types';
import { ImageCarousel } from './ImageCarousel';
import { ProductDetails } from './ProductDetails';
import { ProductDetails as ProspectProductDetails } from './prospect/ProductDetails';
import { Description } from './prospect/Description';
import { Alert } from './prospect/Alert';
import { Instructions } from './Instructions';
import { Recommendations } from './Recommendations';
import { Ingredients } from './Ingredients';
import { CustomerReviews } from './ReviewsContent/CustomerReviews';

import { Breadcrumb } from './Breadcrumb';
import styles from './ProductDisplayPage.module.scss';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { gtmTrackViewItem } from '../../services/gtm/gtm';
import { useAuth } from '../../contexts/AuthContext';
import {
  useRecommendationsWithMenuCartItems,
} from '../../hooks/useRecommendationsWithMenuCartItems';

function ProductDisplayPage(): ReactElement {
  const { slug } = useParams();
  const location = useLocation();
  const { skuId } = location?.state as { skuId: number } || {};
  const searchParams = new URLSearchParams(location.search);
  const instructionsExpanded = searchParams.get('recipe_card_view') === 'true';
  const { data: product } = useProductItemQuery(slug ?? '');

  const { isLoggedIn } = useAuth();

  const {
    menuWithCartItems,
    cart,
  } = useMenuWithCart();
  const { data: productRecommendations } = useProductRecommendationsQuery(slug ?? '', isLoggedIn);
  const recommendationsWithCartItems = useRecommendationsWithMenuCartItems(
    productRecommendations?.recommendations, menuWithCartItems,
  );

  const sku = product?.skus?.find((item) => item.id === skuId)
    // if skuId is not provided, use the smallest serving size sku assoc with the product
    || product?.skus.reduce((prev, curr) => (prev.servings < curr.servings ? prev : curr));
  const menuCartItem = menuWithCartItems?.find((item) => item.skuId === sku?.id) || null;

  const { user } = useUser();
  const isInactiveSubscription = user?.status
    && (user.status === SubscriptionStatus.RETURNING
      || user.status === SubscriptionStatus.CANCELLED);
  const stackElements = useMediaQuery({ query: BreakpointMedia.MAX_MD });
  const isRte = sku?.product_type_name?.toLowerCase() === 'ready to eat' || sku?.product_type_name?.toLowerCase() === 'ready-to-eat';
  const hasTrackedView = useRef<string | null>(null);
  useEffect(() => {
    if (product && sku) {
      const trackKey = `${slug}_${sku.id}`;
      if (hasTrackedView.current !== trackKey) {
        if (!isLoggedIn) {
          gtmTrackViewItem(product, sku);
        }
        hasTrackedView.current = trackKey;
      }
    }
  }, [product, sku, slug, isLoggedIn]);
  const shouldShowBanner = isInactiveSubscription || cart?.isSkipped;
  const flags = useFlags(['display-customer-reviews-pdp', 'pdp_cross_sale']);
  const isPDPCrossSaleEnabled = flags.pdp_cross_sale.enabled;
  const showCustomerReviewsPdp = flags['display-customer-reviews-pdp'].enabled && flags['display-customer-reviews-pdp'].value === 'show';
  const showProspectPDP = !isLoggedIn && !menuCartItem && sku && product;
  const heapPdpAvailability = menuCartItem ? 'available' : `off-menu${isLoggedIn ? ' logged in' : ''}`;

  return (
    <>
      {shouldShowBanner && <ReactivationBannerContainer />}
      <div className={styles.pdp_container} data-heap-PDP-availability={heapPdpAvailability}>
        {stackElements && product && (
          <>
            <Breadcrumb pageTitle={product.title} />
            {showProspectPDP && <Alert />}
          </>
        )}
        <div className={styles.main_content_container}>
          {!!sku?.images?.length && (
            <ImageCarousel
              images={sku.images}
              skuId={sku.id}
              isFavorite={sku.favorite}
            />
          )}
          {sku && product && (
            showProspectPDP ? (
              <ProspectProductDetails sku={sku} product={product} />
            ) : (
              <ProductDetails
                sku={sku}
                product={product}
                isRte={isRte}
                menuCartItem={menuCartItem}
              />
            )
          )}
        </div>
        {showProspectPDP ? (
          <>
            <Description sku={sku} product={product} />
            {!!recommendationsWithCartItems?.length && isPDPCrossSaleEnabled && (
              <Recommendations
                recommendations={recommendationsWithCartItems}
                header="Similar items available now:"
              />
            )}
            {!!sku?.ingredients?.length && (
              <Ingredients
                ingredients={sku.ingredients}
                productType={sku.product_type_name}
                defaultOpen={false}
              />
            )}
            {!!sku?.instructions?.length && (
              <Instructions
                instructions={sku.instructions}
                initiallyExpanded={false}
              />
            )}
            {showCustomerReviewsPdp && (
              <>
                <div id="customer-reviews" />
                <CustomerReviews product={product} defaultOpen={false} />
              </>
            )}
          </>
        ) : (
          <>
            {!!sku?.ingredients?.length && (
              <Ingredients ingredients={sku.ingredients} productType={sku.product_type_name} />
            )}
            {!!sku?.instructions?.length && (
              <Instructions
                instructions={sku.instructions}
                initiallyExpanded={instructionsExpanded}
              />
            )}
            {!!recommendationsWithCartItems?.length && isPDPCrossSaleEnabled && (
              <Recommendations recommendations={recommendationsWithCartItems} />
            )}
            {showCustomerReviewsPdp && sku && product && (
              <>
                <div id="customer-reviews" />
                <CustomerReviews product={product} />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default ProductDisplayPage;
